/**
 * Which requests the service worker may cache, and how. Pure functions (no worker globals), so the
 * rules are unit tested and the worker only wires them to strategies.
 *
 * The default is NO: anything not named here is passed to the network untouched.
 */

export type RouteKind =
  /** `/api/spots?status=published`, `/api/lakes`: stale-while-revalidate. */
  | "list"
  /** `/api/spots/:slug`: network first (3 s), cache fallback. */
  | "detail"
  /** `/api/terrain`: cache first, but only what a saved spot put there. */
  | "terrain"
  /** swisstopo WMTS tiles: cache first, LRU. */
  | "tile"
  /** swisstopo WMS (swissimage texture): cache first. */
  | "wms"
  /** Never read from or written to any cache. */
  | "never"
  /** Not ours (app shell, assets): handled by precaching / the browser. */
  | "other";

export interface RuleInput {
  url: URL;
  method: string;
  /** `self.location.origin` of the worker. */
  origin: string;
  /** The page that made the request (`request.referrer`); admin pages never feed the caches. */
  referrer?: string;
}

/** Cache names. Bump the suffix to drop an old layout. */
export const CACHES = {
  lists: "divespots-api-lists-v1",
  detail: "divespots-api-spot-v1",
  tiles: "divespots-tiles-v1",
  wms: "divespots-wms-v1",
  /** What "Offline speichern" stored. Not evicted automatically. */
  saved: "divespots-saved-v1",
} as const;

export const TILE_LIMIT = 2000;
export const WMS_LIMIT = 80;
export const DETAIL_TIMEOUT_S = 3;

const NEVER_API = [
  /^\/api\/auth(\/|$)/,
  /^\/api\/me(\/|$)/,
  /^\/api\/admin(\/|$)/,
  /^\/api\/suggestions/,
];
const ADMIN_PAGE = /^\/[a-z]{2}\/admin(\/|$)/;
const TILE_PATH =
  /^(?:\/wmts)?\/1\.0\.0\/[\w.-]+\/default\/current\/3857\/\d+\/\d+\/\d+\.(?:jpeg|png)$/;

const SWISSTOPO_TILE_HOST = "wmts.geo.admin.ch";
const SWISSTOPO_WMS_HOST = "wms.geo.admin.ch";

function referrerIsAdmin(referrer: string | undefined, origin: string): boolean {
  if (!referrer) return false;
  try {
    const r = new URL(referrer);
    return r.origin === origin && ADMIN_PAGE.test(r.pathname);
  } catch {
    return false;
  }
}

export function classify({ url, method, origin, referrer }: RuleInput): RouteKind {
  const sameOrigin = url.origin === origin;

  if (sameOrigin && url.pathname.startsWith("/api/")) {
    if (method !== "GET") return "never";
    if (NEVER_API.some((re) => re.test(url.pathname))) return "never";
    if (referrerIsAdmin(referrer, origin)) return "never";
    const params = [...url.searchParams.keys()];
    if (url.pathname === "/api/spots") {
      // the public list only: an unfiltered list contains drafts for staff
      return params.length === 1 && url.searchParams.get("status") === "published"
        ? "list"
        : "never";
    }
    if (url.pathname === "/api/lakes") return params.length === 0 ? "list" : "never";
    if (/^\/api\/spots\/[^/]+$/.test(url.pathname)) return params.length === 0 ? "detail" : "never";
    if (url.pathname === "/api/terrain") return "terrain";
    return "never";
  }
  if (method !== "GET") return "never";

  const tileHost = url.hostname === SWISSTOPO_TILE_HOST || sameOrigin;
  if (tileHost && TILE_PATH.test(url.pathname)) return "tile";

  const wmsHost = url.hostname === SWISSTOPO_WMS_HOST || (sameOrigin && url.pathname === "/wms/");
  if (wmsHost) {
    const q = new Map([...url.searchParams].map(([k, v]) => [k.toLowerCase(), v]));
    if (
      q.get("service")?.toUpperCase() === "WMS" &&
      q.get("request")?.toLowerCase() === "getmap" &&
      q.get("layers") === "ch.swisstopo.swissimage"
    ) {
      return "wms";
    }
  }
  return "other";
}

/** True when a response may be stored: a complete 200, nothing personalised. */
export function isCacheableResponse(
  status: number,
  headers: { get(name: string): string | null },
): boolean {
  if (status !== 200) return false;
  const cc = (headers.get("cache-control") ?? "").toLowerCase();
  if (/\bno-store\b/.test(cc)) return false;
  return !headers.get("set-cookie");
}
